import React from "react";
import { Box, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import BracketMatchCard from "./BracketMatchCard";
import type { PlayerDirectory, Round } from "./types";
import styles from "./Resultats.module.css";

interface BracketRoundProps {
  round: Round;
  players: PlayerDirectory;
  highlightedLicence?: string | null;
  pathMatchIds?: Set<string>;
  onSelectLicence?: (licence: string) => void;
  /** Nombre de matchs du premier tour (pour l’alignement vertical). */
  firstRoundMatchCount?: number;
}

export default function BracketRound({
  round,
  players,
  highlightedLicence = null,
  pathMatchIds,
  onSelectLicence,
  firstRoundMatchCount,
}: BracketRoundProps): React.ReactElement {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const span =
    firstRoundMatchCount && round.matches.length > 0
      ? Math.max(1, Math.round(firstRoundMatchCount / round.matches.length))
      : 1;

  return (
    <Box
      className={styles.bracketRound}
      component="section"
      style={{ "--bracket-span": span } as React.CSSProperties}
    >
      <Typography
        className={styles.bracketRoundTitle}
        component="h3"
        sx={{
          color: isDark ? "secondary.light" : "primary.dark",
          bgcolor: isDark
            ? alpha(theme.palette.secondary.main, 0.14)
            : alpha(theme.palette.primary.main, 0.1),
        }}
      >
        {round.label}
      </Typography>
      <Box className={styles.bracketMatches}>
        {round.matches.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: 0.5 }}>
            Aucun match
          </Typography>
        ) : (
          round.matches.map((game) => (
            <Box
              key={game.id}
              className={styles.bracketMatchSlot}
              style={{ flex: span }}
            >
              <BracketMatchCard
                game={game}
                players={players}
                highlightedLicence={highlightedLicence}
                onSelectLicence={onSelectLicence}
                pathHighlighted={pathMatchIds?.has(game.id) ?? false}
              />
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
}
